import React from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { UseOfForceReportIncidentModel, UseOfForceReportModel, UseOfForceSupervisor } from '../../api-service/clients';
import { Accordion, AccordionDetails, AccordionSummary } from '../../components/accordion';
import { formatCpr, formatDate, formatDateTime } from '../../utilities';
import { interventionTypeShortLabels, supervisorLabels } from '../citizen/serviceprovider/use-of-force/use-of-force-labels';
import { getUseOfForceReportPdf } from './use-of-force-report-api';

interface UseOfForceSentReportsProps {
  serviceProviderUnitId: string;
  reports: UseOfForceReportModel[];
  onOpenIncident: (incident: UseOfForceReportIncidentModel) => void;
}

export function UseOfForceSentReports(props: UseOfForceSentReportsProps) {
  const { serviceProviderUnitId, reports, onOpenIncident } = props;
  if (reports.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Sendte indberetninger
      </Typography>
      {reports.map(report => (
        <Accordion key={report.id} sx={{ mb: 1 }}>
          <AccordionSummary>
            {[report.sentToSupervisionAt ? `Sendt ${formatDateTime(report.sentToSupervisionAt)}` : 'Sendt', supervisorText(report), incidentCount(report.incidents.length)].join(
              ' · '
            )}
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <Box>
                <Button variant="outlined" size="small" onClick={() => openReportPdf(serviceProviderUnitId, report.id)}>
                  Hent PDF
                </Button>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5 }}>
                <LabelValue label="Tilsyn" value={supervisorText(report)} />
                <LabelValue label="Sendt til tilsynet" value={report.sentToSupervisionAt ? formatDateTime(report.sentToSupervisionAt) : '-'} />
                <LabelValue
                  label="Driftsherre"
                  value={report.header.operatorName ? `${report.header.operatorName}${report.header.operatorCvr ? ` (CVR ${report.header.operatorCvr})` : ''}` : '-'}
                />
                <LabelValue label="Driftsherre orienteret" value={formatDate(report.operatorInformedAt, undefined, '-')} />
              </Box>
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '20%' }}>Indgreb påbegyndt</TableCell>
                    <TableCell sx={{ width: '40%' }}>Borger</TableCell>
                    <TableCell>Indgreb</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.incidents.map(incident => (
                    <TableRow key={incident.id} hover onClick={() => onOpenIncident(incident)} sx={{ cursor: 'pointer' }}>
                      <TableCell>{incident.interventionStartedAt ? formatDateTime(incident.interventionStartedAt) : '-'}</TableCell>
                      <TableCell>
                        {incident.citizenName ?? '-'}
                        {incident.citizenCpr && (
                          <Typography variant="caption" display="block">
                            {formatCpr(incident.citizenCpr)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{incident.interventionType ? interventionTypeShortLabels[incident.interventionType] : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export function supervisorText(report: UseOfForceReportModel): string {
  const { supervisor } = report.header;
  if (!supervisor) return 'Tilsyn ikke valgt';
  if (supervisor === UseOfForceSupervisor.Municipality && report.supervisionMunicipalityName) return report.supervisionMunicipalityName;
  return supervisorLabels[supervisor];
}

export function incidentCount(count: number): string {
  return count === 1 ? '1 skema' : `${count} skemaer`;
}

function LabelValue(props: { label: string; value: string }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        {props.label}
      </Typography>
      <Typography variant="body2">{props.value}</Typography>
    </>
  );
}

export async function openReportPdf(serviceProviderUnitId: string, reportId: string) {
  const pdf = await getUseOfForceReportPdf(serviceProviderUnitId, reportId);
  window.open(URL.createObjectURL(pdf), '_blank');
}
